import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../../components/pageHeader";
import ConfirmationModal from "../../components/confirmDeleteModal";
import LoadingOverlay from "../../components/loadingOverlay";
import { API_BASE } from "../../config/apiConfig";
import { fromFirestoreTimestamp, formatDateTime } from "../../utils/date";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import MainContentLayout from "../../components/mainContentLayout";
import { deleteLog } from "../../features/ptLogs/ptLogsSlice";

export default function ViewPTLog({ mode = "player" }) {
  const { id, playerId } = useParams();
  const token = sessionStorage.getItem("access_token");
  const user = useSelector((state) => state.user.user);

  const location = useLocation();
  const from = location.state?.from;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);

  // -----------------------------------------------
  // Fetch log from backend
  // -----------------------------------------------
  useEffect(() => {
    async function loadLog() {
      setLoading(true);

      try {
        const res = await fetch(`${API_BASE}/api/pt-log/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();

        if (!data.success) {
          console.error("Failed to fetch log:", data.message);
          return;
        }

        setLog(data.log);
      } catch (err) {
        console.error("View PT log error:", err);
      } finally {
        setLoading(false);
      }
    }

    loadLog();
  }, [id]);

  if (loading) return <LoadingOverlay show={true} text="Loading..." />;
  if (!log) return <p className="p-4">Failed to load log.</p>;

  // Convert timestamp
  const jsDate = fromFirestoreTimestamp(log.date);
  const dateStr = jsDate?.toLocaleDateString("en-GB");
  const timeStr = jsDate?.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // -----------------------------------------------
  // Delete
  // -----------------------------------------------
  async function handleDelete() {
    try {
      const res = await fetch(`${API_BASE}/api/pt-log/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (!data.success) {
        console.error("Delete error:", data.message);
        return;
      }

      const year = jsDate.getFullYear();
      const month = jsDate.getMonth() + 1;

      const key = `player-${user.user_id}-${year}-${month}`;

      dispatch(deleteLog({ key, logId: id }));

    } catch (err) {
      console.error("Delete PT log error:", err);
    } finally {
      setShowConfirm(false);
      navigate("/pt-logs-list");
    }
  }

  let backTo = "/pt-logs-list";

  if (from === "pending-review") {
    backTo = "/pending-review-pt-log"
  } else if (mode === "player") {
    backTo = "/pt-logs-list"
  } else {
    backTo = `/pt-logs-list/${playerId}`
  };

  return (
    <MainContentLayout>
      <PageHeader
        title="Training Log"
        backTo={backTo}
      />

      {/* Date + Buttons */}
      <div className="flex items-center justify-between mb-4 text-gray-500">
        <div>
          <span className="mr-4">Date: {dateStr}</span>
          <span>Time: {timeStr}</span>
        </div>

        {mode === "player" && (
          <div className="flex gap-3">
            <button
              className="text-blue-600 hover:text-blue-800"
              onClick={() => navigate(`/edit-pt-log/${id}`, { state: { log, from } })}
            >
              <FaEdit size={18} />
            </button>

            <button
              className="text-red-600 hover:text-red-800"
              onClick={() => setShowConfirm(true)}
            >
              <FaTrash size={18} />
            </button>
          </div>
        )}

        {mode === "coach" && (
          <button
            className="text-blue-600 hover:text-blue-800"
            onClick={() =>
              navigate(`/edit-pt-log/${id}`, { state: { log, from } })
            }
          >
            <FaEdit size={18} />
          </button>
        )}
      </div>

      {/* Q&A */}
      <div className="space-y-6 max-h-[75vh] overflow-y-auto text-left">
        {log.answers?.length ? (
          log.answers.map((item, idx) => (
            <div key={idx}>
              <p className="font-bold text-sm mb-1">
                {idx + 1}. {item.question}
              </p>
              <div className="border rounded p-3 bg-gray-50 text-gray-700 whitespace-pre-wrap">
                {item.answer}
              </div>
            </div>
          ))
        ) : (
          <p className="italic text-gray-400">No entries found.</p>
        )}
      </div>

      {/* Coach Comment Section */}
      <div className="mt-6 text-left">
        <h3 className="text-sm mb-1">Coach’s Comment</h3>

        <div className="border rounded p-3 bg-gray-50 text-gray-700 whitespace-pre-wrap">
          {log.coachComment ? log.coachComment : ""}
        </div>

        {/* Commented Date (show only if exists) */}
        {log.commentedAt && (
          <p className="text-xs text-gray-500 mt-1">
            Commented on:{" "}
            {formatDateTime(log.commentedAt)}
          </p>
        )}
      </div>

      {/* Delete confirm modal */}
      <ConfirmationModal
        show={showConfirm}
        text="Are you sure you want to delete this training log?"
        onConfirm={handleDelete}
        onCancel={() => setShowConfirm(false)}
      />
    </MainContentLayout>
  );
}
